import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IRouteAccessState, RouteAccessDTO } from "../../../types/access";

/**
 * 🧱 Estado inicial del control de acceso a rutas protegidas.
 *
 * Define los valores predeterminados para el estado global de navegación,
 * garantizando que al iniciar la aplicación:
 * - No existan validaciones previas.
 * - No haya bloqueos de transición activos.
 * - La ruta visible corresponda al contexto inicial.
 */
export const initialState: IRouteAccessState = {
  /** Resultado de la última validación de acceso (true / false / null si aún no se verificó). */
  allowed: null,

  /** Última ruta verificada exitosamente por el sistema. */
  lastCheckedRoute: null,

  /** Ruta actualmente activa y visible para el usuario. */
  currentRoute: null,

  /** Indica si el sistema está procesando una nueva navegación o validación de acceso. */
  isNavigating: false,

  /** Controla si la transición entre rutas está en pausa (esperando confirmación). */
  holdTransition: false,
};

/**
 * 🧭 Slice de control de acceso a rutas protegidas
 * ------------------------------------------------------------
 * Gestiona el estado global relacionado con:
 * - Validación de permisos de usuario sobre rutas protegidas.
 * - Control de navegación y transiciones entre páginas.
 * - Pausas de navegación durante confirmaciones o procesos pendientes.
 *
 * Este slice trabaja en conjunto con:
 * - `useRouteAccess`: hook que provee las acciones (`checkAccess`, `hold`, `release`).
 * - `ProtectedRouteV2`: componente que decide si se debe renderizar la nueva ruta,
 *    mantener la actual, o bloquear la navegación.
 */
export const routeAccessSlice = createSlice({
  name: "routeAccess",
  initialState,
  reducers: {
    // =====================================================
    // 🌐 VALIDACIÓN DE ACCESO
    // =====================================================

    /**
     * 🔹 Actualiza el resultado de validación de acceso y marca
     * la última ruta verificada por el sistema.
     *
     * Este método se ejecuta cuando `RouteAccessService.checkAccess()`
     * responde desde el backend, indicando si el usuario tiene permiso
     * para acceder a la nueva ruta.
     *
     * - Si la ruta está permitida (`allowed: true`), también se actualiza
     *   `currentRoute` para reflejar que ya puede mostrarse al usuario.
     * - Si el acceso es denegado, `currentRoute` permanece sin cambios.
     *
     * @example
     * dispatch(setAccessResult({
     *   route: '/purchases/register',
     *   result: { allowed: true }
     * }));
     */
    setAccessResult: (
      state,
      action: PayloadAction<{ route: string; result: RouteAccessDTO }>
    ) => {
      const { route, result } = action.payload;
      state.allowed = result.allowed;
      state.lastCheckedRoute = route;
      state.isNavigating = false;

      // ✅ Si se permite el acceso, la ruta actual se actualiza
      if (result.allowed) {
        state.currentRoute = route;
      }

      // 🧹 Al completar la validación, se libera cualquier pausa activa
      state.holdTransition = false;
    },

    /**
     * 🔄 Reinicia completamente el estado global de validación y navegación.
     *
     * Se usa principalmente cuando:
     * - El usuario cierra sesión.
     * - Se reinicia la sesión o el contexto del sistema.
     * - Se desea limpiar el estado de rutas protegidas al resetear el store.
     */
    resetAccessState: () => initialState,

    // =====================================================
    // ⚙️ ESTADOS DE TRANSICIÓN Y BLOQUEO
    // =====================================================

    /**
     * 🚀 Marca el inicio o finalización de una validación de acceso a rutas.
     *
     * Permite controlar visualmente los estados de carga o los bloqueos
     * mientras se verifica el permiso del usuario.
     *
     * @example
     * dispatch(setNavigating(true));  // Inicia validación
     * dispatch(setNavigating(false)); // Finaliza validación
     */
    setNavigating: (state, action: PayloadAction<boolean>) => {
      state.isNavigating = action.payload;
    },

    /**
     * ⏸️ Pausa o libera la transición global entre rutas.
     *
     * Este estado permite detener la navegación mientras se espera
     * una confirmación del usuario o una acción pendiente (por ejemplo,
     * un modal de “¿Deseas abandonar esta página?”).
     *
     * @example
     * dispatch(setHoldTransition(true));  // Pausar navegación
     * dispatch(setHoldTransition(false)); // Reanudar navegación
     */
    setHoldTransition: (state, action: PayloadAction<boolean>) => {
      state.holdTransition = action.payload;
    },

    /**
     * 🧭 Actualiza manualmente la ruta actualmente visible (`currentRoute`).
     *
     * Puede utilizarse en casos donde se necesita forzar la sincronización
     * de la vista actual después de un cambio programático de navegación,
     * o para recuperar el estado anterior cuando una transición fue cancelada.
     *
     * @example
     * dispatch(setCurrentRoute('/dashboard/sales'));
     */
    setCurrentRoute: (state, action: PayloadAction<string>) => {
      state.currentRoute = action.payload;
    },
  },
});

// =====================================================
// 📤 EXPORTAR ACTIONS
// =====================================================

/**
 * Acciones disponibles para manipular el estado de acceso a rutas:
 *
 * - `setAccessResult` → Actualiza resultado de validación.
 * - `resetAccessState` → Reinicia completamente el estado global.
 * - `setNavigating` → Marca inicio o fin de navegación.
 * - `setHoldTransition` → Pausa o libera transición.
 * - `setCurrentRoute` → Actualiza manualmente la ruta visible.
 */
export const {
  setAccessResult,
  resetAccessState,
  setNavigating,
  setHoldTransition,
  setCurrentRoute,
} = routeAccessSlice.actions;

// =====================================================
// 📤 EXPORTAR REDUCER
// =====================================================

/**
 * Reducer principal de control de acceso y navegación entre rutas.
 *
 * Este reducer se combina dentro del store global (`RootState`)
 * y es consumido por `useRouteAccess()` y `ProtectedRouteV2`
 * para coordinar los flujos de validación, bloqueo y transición visual.
 */
export default routeAccessSlice.reducer;
